// VIDEO-EDITING — b-roll ripple-insert timeline (pure math).
//
// A `TimelineOp` records one clip-timeline edit as the whole-segment caption
// shift it implies. `insertBrollAt` / `removeClipRipple` ripple only the
// *active* language's `captions.segments` live; the recorded ops let the
// save path replay the identical shift across every other language
// (transcript + translations[]) so the parallel arrays never drift apart.
import {
  durationOfClip,
  dropAndShiftRanges,
  shiftTimedRanges,
} from "@wiser/remotion-layers";

import type { Clip, TranscriptSegment } from "./project";

export type TimelineOp =
  | { kind: "insert"; atSec: number; delta: number }
  | { kind: "remove"; r0: number; r1: number }
  | { kind: "trim"; atSec: number; delta: number }
  // VIDEO-EDITING — Phase 5. Adding/clearing a crossfade at a clip seam
  // shrinks/lengthens the rendered timeline by `delta`; everything at or
  // after `atSec` (the seam's OLD rendered position) shifts by `delta`.
  // Shares the `shiftTimedRanges` replay path with `insert` / `trim`.
  | { kind: "transition"; atSec: number; delta: number };

export function applyOpToSegments(
  segs: TranscriptSegment[],
  op: TimelineOp,
): TranscriptSegment[] {
  return op.kind === "remove"
    ? (dropAndShiftRanges(segs, op.r0, op.r1) as TranscriptSegment[])
    : (shiftTimedRanges(segs, op.atSec, op.delta) as TranscriptSegment[]);
}

export function applyOpsToSegments(
  segs: TranscriptSegment[],
  ops: TimelineOp[],
): TranscriptSegment[] {
  return ops.reduce(applyOpToSegments, segs);
}

// Snap a raw time to the nearest value in `boundaries` — a b-roll insert
// lands on a caption-segment edge so captions ripple as whole segments and
// never straddle the splice (plan decision 4).
export function snapToBoundary(t: number, boundaries: number[]): number {
  if (boundaries.length === 0) return Math.max(0, t);
  let best = boundaries[0];
  for (const b of boundaries) {
    if (Math.abs(t - b) < Math.abs(t - best)) best = b;
  }
  return best;
}

// Snap targets for a b-roll insert or reorder — caption-segment edges when
// a transcript exists (decision 4: captions ripple as whole segments and
// never straddle a splice), else raw clip boundaries.
export function clipBoundaries(
  segments: { start: number; end: number }[],
  clipsList: Clip[],
  totalDurationSec: number,
): number[] {
  const set = new Set<number>([0]);
  if (segments.length > 0) {
    for (const s of segments) {
      set.add(s.start);
      set.add(s.end);
    }
  } else {
    let off = 0;
    for (const c of clipsList) {
      set.add(off);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      off += durationOfClip(c as any);
    }
  }
  set.add(totalDurationSec);
  return [...set]
    .filter((n) => Number.isFinite(n) && n >= 0)
    .sort((a, b) => a - b);
}
